import { useCallback, useRef } from 'react'

type Resolver = () => void

export type OrgUpdateAwaiter = {
  awaitOrgUpdateOrTimeout: (orgId: string, timeoutMs: number) => Promise<void>
  dispatchOrgUpdate: (orgId: string) => void
}

/**
 * Resolver Map keyed by orgId. Caller awaits either the next dispatched
 * update for that org or a timeout, whichever comes first.
 */
export default function useOrgUpdateAwaiter(): OrgUpdateAwaiter {
  const resolversRef = useRef<Map<string, Resolver>>(new Map())

  const awaitOrgUpdateOrTimeout = useCallback(
    (orgId: string, timeoutMs: number): Promise<void> =>
      new Promise<void>((resolve) => {
        let resolved = false
        const finish = (): void => {
          if (resolved) return
          resolved = true
          // Identity-check before deleting: a later wait on the same orgId
          // may have replaced this entry, and we must not evict it.
          if (resolversRef.current.get(orgId) === finish) {
            resolversRef.current.delete(orgId)
          }
          clearTimeout(timer)
          resolve()
        }
        resolversRef.current.set(orgId, finish)
        const timer = setTimeout(finish, timeoutMs)
      }),
    [],
  )

  const dispatchOrgUpdate = useCallback((orgId: string): void => {
    const resolver = resolversRef.current.get(orgId)
    if (resolver) {
      resolversRef.current.delete(orgId)
      resolver()
    }
  }, [])

  return { awaitOrgUpdateOrTimeout, dispatchOrgUpdate }
}
